/**
 * Knowledge tab — what an agent draws on when it answers, per agent, read-only:
 *  - Domains: curated, structured knowledge it reads (name, introduction, agent-specific
 *    or shared, last published). Never a domain's files or body.
 *  - Knowledge bases: documents it searches (name, introduction, latest sync).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GatewayApiError } from '../../client/GatewayClient';
import { useLocale } from '../../i18n/useLocale';
import { listDomains, type Domain } from './domains';
import { listKnowledgeBases, pickText, type KnowledgeBase } from './knowledgeBases';
import { SETTINGS_AGENTS } from './settingsAgents';

interface Section<T> {
  items: T[];
  failed: boolean;
}

const isForbidden = (e: unknown) => e instanceof GatewayApiError && e.statusCode === 403;

export default function KnowledgeTab() {
  const { t } = useTranslation('settings');
  const { locale } = useLocale();
  const [agent, setAgent] = useState('rocky');
  const [domains, setDomains] = useState<Section<Domain>>({ items: [], failed: false });
  const [kbs, setKbs] = useState<Section<KnowledgeBase>>({ items: [], failed: false });
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  // Switching agents quickly can resolve requests out of order; keep only the newest
  // per section (a section's Retry bumps only its own counter).
  const domainReq = useRef(0);
  const kbReq = useRef(0);

  const loadDomains = useCallback(async () => {
    const reqId = ++domainReq.current;
    try {
      const items = await listDomains(agent);
      if (reqId === domainReq.current) setDomains({ items, failed: false });
    } catch (e) {
      if (reqId !== domainReq.current) return;
      if (isForbidden(e)) setForbidden(true);
      setDomains({ items: [], failed: true });
    }
  }, [agent]);

  const loadKbs = useCallback(async () => {
    const reqId = ++kbReq.current;
    try {
      const items = await listKnowledgeBases(agent);
      if (reqId === kbReq.current) setKbs({ items, failed: false });
    } catch (e) {
      if (reqId !== kbReq.current) return;
      if (isForbidden(e)) setForbidden(true);
      setKbs({ items: [], failed: true });
    }
  }, [agent]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setForbidden(false);
    Promise.all([loadDomains(), loadKbs()]).finally(() => {
      if (current) setLoading(false);
    });
    return () => {
      current = false;
    };
  }, [loadDomains, loadKbs]);

  const fmt = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(locale === 'en' ? 'en-US' : 'zh-CN');
  };

  const failedNotice = (message: string, retry: () => void) => (
    <div className="memory-tab__error" role="alert">
      {message}{' '}
      <button type="button" className="memory-tab__load-more" onClick={() => retry()}>
        {t('knowledge.retry')}
      </button>
    </div>
  );

  return (
    <div className="memory-tab knowledge-tab">
      <p className="knowledge-tab__intro">{t('knowledge.intro')}</p>
      <div className="memory-tab__controls">
        <label className="memory-tab__control">
          <span className="memory-tab__control-label">{t('knowledge.agent')}</span>
          <select className="memory-tab__select" value={agent} onChange={(e) => setAgent(e.target.value)}>
            {SETTINGS_AGENTS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="memory-tab__loading">{t('knowledge.loading')}</div>
      ) : forbidden ? (
        <div className="memory-tab__placeholder">{t('knowledge.forbidden')}</div>
      ) : (
        <>
          <section className="knowledge-tab__section" aria-labelledby="knowledge-domains">
            <h3 id="knowledge-domains" className="knowledge-tab__section-title">{t('knowledge.domainsTitle')}</h3>
            <p className="knowledge-tab__section-intro">{t('knowledge.domainsIntro')}</p>
            {domains.failed ? (
              failedNotice(t('knowledge.domainsLoadFailed'), loadDomains)
            ) : domains.items.length === 0 ? (
              <div className="memory-tab__placeholder">{t('knowledge.domainsEmpty')}</div>
            ) : (
              <div className="knowledge-tab__list">
                {domains.items.map((d) => (
                  <div key={d.name} className="knowledge-tab__card">
                    <div className="knowledge-tab__card-head">
                      <span className="knowledge-tab__name">{pickText(d.displayName, locale, d.name)}</span>
                      <span className={`knowledge-tab__scope knowledge-tab__scope--${d.scope}`}>
                        {t(`knowledge.scope.${d.scope}`)}
                      </span>
                    </div>
                    {pickText(d.description, locale) && (
                      <div className="knowledge-tab__desc">{pickText(d.description, locale)}</div>
                    )}
                    {d.publishedAt && (
                      <div className="knowledge-tab__meta">
                        <span>{t('knowledge.lastPublished', { time: fmt(d.publishedAt) })}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="knowledge-tab__section" aria-labelledby="knowledge-kbs">
            <h3 id="knowledge-kbs" className="knowledge-tab__section-title">{t('knowledge.kbTitle')}</h3>
            <p className="knowledge-tab__section-intro">{t('knowledge.kbIntro')}</p>
            {kbs.failed ? (
              failedNotice(t('knowledge.loadFailed'), loadKbs)
            ) : kbs.items.length === 0 ? (
              <div className="memory-tab__placeholder">{t('knowledge.empty')}</div>
            ) : (
              <div className="knowledge-tab__list">
                {kbs.items.map((kb) => (
                  <div key={kb.name} className="knowledge-tab__card">
                    <div className="knowledge-tab__card-head">
                      <span className="knowledge-tab__name">{pickText(kb.displayName, locale, kb.name)}</span>
                      {kb.sync && (
                        <span className={`knowledge-tab__status knowledge-tab__status--${kb.sync.status}`}>
                          {t(`knowledge.status.${kb.sync.status}`)}
                        </span>
                      )}
                    </div>
                    {pickText(kb.description, locale) && (
                      <div className="knowledge-tab__desc">{pickText(kb.description, locale)}</div>
                    )}
                    {kb.sync && (
                      <div className="knowledge-tab__meta">
                        {kb.sync.documents != null && (
                          <>
                            <span>{t('knowledge.documents', { n: kb.sync.documents })}</span>
                            <span className="capabilities-tab__dot">·</span>
                          </>
                        )}
                        <span>{t('knowledge.lastSynced', { time: fmt(kb.sync.updatedAt) })}</span>
                      </div>
                    )}
                    {kb.sync?.status === 'failed' && (
                      <div className="knowledge-tab__hint">{t('knowledge.failedHint')}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
